import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, FileDown, RefreshCw, Download, Copy, Check, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiService } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationStore } from '@/stores/organizationStore';
import {
  MedicalNote,
  MedicalNoteMetadata,
  type MedicalNoteSection,
  MEDICAL_NOTE_SECTION_LABELS,
  MEDICAL_NOTE_SECTION_COLORS,
  MEDICAL_NOTE_SECTION_ORDER
} from '@/types/medicalNote';
import toast from 'react-hot-toast';

// How long a downloaded PDF's object URL stays valid (see downloadPdf).
const PDF_URL_LIFETIME_MS = 60_000;

interface MedicalNotePreviewProps {
  appointmentId: string;
  isEncounterMode?: boolean;
  onNoteUpdate?: (note: MedicalNote) => void;
  hasPendingChanges?: boolean;
  onNoteGenerated?: (timestamp: string) => void;
  onNoteLoaded?: (noteId: string, metadata: MedicalNoteMetadata) => void;
  /** Set by the page once it signs the note, so the PDF export appears without a reload. */
  isSigned?: boolean;
}

export const MedicalNotePreview: React.FC<MedicalNotePreviewProps> = ({
  appointmentId,
  isEncounterMode = false,
  onNoteUpdate,
  hasPendingChanges = false,
  onNoteGenerated,
  onNoteLoaded,
  isSigned = false
}) => {
  const [note, setNote] = useState<MedicalNote | null>(null);
  const [metadata, setMetadata] = useState<MedicalNoteMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [soapNote, setSoapNote] = useState<string | null>(null);
  const [soapLoading, setSoapLoading] = useState(false);
  // Staff can read a note but not create one: never ask to generate on their behalf
  const { isReadOnly } = useAuth();
  const readOnly = isReadOnly();
  const { operationMode } = useOrganizationStore();
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Load or generate medical note on mount
  useEffect(() => {
    if (appointmentId) {
      loadOrGenerateMedicalNote();
    }
  }, [appointmentId, isEncounterMode]);

  // Try to load existing note, generate only if not found
  const loadOrGenerateMedicalNote = async () => {
    setLoading(true);
    setError(null);

    // Build endpoint based on mode
    const baseEndpoint = isEncounterMode
      ? `/v1/clinical/encounter/${appointmentId}/medical-note`
      : `/v1/clinical/${appointmentId}/medical-note`;

    try {
      // First, try to fetch existing medical note from database
      const getResponse = await apiService.get<{
        success: boolean;
        found?: boolean;
        data: {
          noteId: string;
          note: MedicalNote;
          metadata: MedicalNoteMetadata;
        } | null;
        canGenerate?: boolean;
      }>(baseEndpoint);

      if (getResponse.success && getResponse.found !== false && getResponse.data) {
        console.log('📄 Loaded existing medical note from database');
        setNote(getResponse.data.note);
        setMetadata(getResponse.data.metadata);
        onNoteUpdate?.(getResponse.data.note);
        onNoteLoaded?.(getResponse.data.noteId, getResponse.data.metadata);
        if (getResponse.data.metadata.generatedAt) {
          onNoteGenerated?.(getResponse.data.metadata.generatedAt);
        }
      } else if (getResponse.found === false) {
        // Note doesn't exist yet — generate one (not an error, expected flow)
        console.log('📝 No existing note found, generating new one...');
        if (getResponse.canGenerate !== false && !readOnly) {
          await generateMedicalNote();
        }
      }
    } catch (err: any) {
      // Legacy 404 handling (fallback for backwards compatibility)
      if (err?.response?.status === 404 || (err instanceof Error && err.message.includes('404'))) {
        console.log('📝 No existing note found, generating new one...');
        const canGenerate = err?.response?.data?.canGenerate !== false;
        if (canGenerate && !readOnly) {
          await generateMedicalNote();
        } else if (!readOnly) {
          setError(err?.response?.data?.error || 'Failed to load medical note');
        }
      } else {
        // Other errors
        console.error('Error loading medical note:', err);
        setError(err instanceof Error ? err.message : 'Failed to load medical note');
      }
    } finally {
      setLoading(false);
    }
  };

  // Force generate new medical note (called by Regenerate button)
  const generateMedicalNote = async () => {
    if (readOnly) return;
    setLoading(true);
    setError(null);

    // Build endpoint based on mode
    const generateEndpoint = isEncounterMode
      ? `/v1/clinical/encounter/${appointmentId}/medical-note/generate`
      : `/v1/clinical/${appointmentId}/medical-note/generate`;

    try {
      const response = await apiService.post<{
        success: boolean;
        data: {
          noteId: string;
          note: MedicalNote;
          metadata: MedicalNoteMetadata;
          status: 'draft' | 'pending' | 'signed';
          wasRegenerated: boolean;
        };
      }>(generateEndpoint, {
        style: 'standard',
        includePlainText: true
      });

      if (response.success) {
        console.log('🔍 Medical note generated:', {
          noteId: response.data.noteId,
          wasRegenerated: response.data.wasRegenerated,
          sampleSection: response.data.note.chiefComplaint
        });
        setNote(response.data.note);
        setMetadata(response.data.metadata);
        onNoteUpdate?.(response.data.note);
        onNoteLoaded?.(response.data.noteId, response.data.metadata);

        // Notify parent of note generation time
        const generatedAt = new Date().toISOString();
        onNoteGenerated?.(generatedAt);

        // If SOAP note exists, regenerate it too
        if (soapNote) {
          const newSoap = formatNoteAsSOAP(response.data.note);
          setSoapNote(newSoap);
        }

        if (response.data.wasRegenerated) {
          toast.success('Medical note regenerated');
        } else {
          toast.success('Medical note generated successfully');
        }
      } else {
        throw new Error('Failed to generate medical note');
      }
    } catch (err: any) {
      console.error('Medical note generation error:', err);

      // Better error messages from backend
      const errorMessage = err?.response?.data?.error ||
                          (err instanceof Error ? err.message : 'Failed to generate medical note');
      const helpMessage = err?.response?.data?.help;

      setError(errorMessage);

      // Show actionable toast message
      if (helpMessage) {
        toast.error(`${errorMessage}. ${helpMessage}`, { duration: 5000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!note) return;

    const sections = MEDICAL_NOTE_SECTION_ORDER
      .filter(key => note[key] !== undefined)
      .map(key => [key, note[key]] as [keyof MedicalNote, MedicalNoteSection]);
    const fullNote = `MEDICAL DOCUMENTATION NOTE
Generated: ${metadata?.generatedAt ? new Date(metadata.generatedAt).toLocaleString() : 'N/A'}

${sections.map(([key, section]) => {
  const label = MEDICAL_NOTE_SECTION_LABELS[key];
  if (section.status === 'not_captured' || !section.content) {
    return `${label.toUpperCase()}:\n[Not documented in transcript]`;
  }
  let sectionText = `${label.toUpperCase()}:\n${section.content}`;
  if (section.physicianNotes) {
    sectionText += `\n\nPhysician Notes:\n${section.physicianNotes}`;
  }
  return sectionText;
}).join('\n\n')}`;

    navigator.clipboard.writeText(fullNote);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadNote = () => {
    if (!note) return;

    const sections = MEDICAL_NOTE_SECTION_ORDER
      .filter(key => note[key] !== undefined)
      .map(key => [key, note[key]] as [keyof MedicalNote, MedicalNoteSection]);
    const fullNote = `MEDICAL DOCUMENTATION NOTE
Generated: ${metadata?.generatedAt ? new Date(metadata.generatedAt).toLocaleString() : 'N/A'}

${sections.map(([key, section]) => {
  const label = MEDICAL_NOTE_SECTION_LABELS[key];
  if (section.status === 'not_captured' || !section.content) {
    return `${label.toUpperCase()}:\n[Not documented in transcript]`;
  }
  let sectionText = `${label.toUpperCase()}:\n${section.content}`;
  if (section.physicianNotes) {
    sectionText += `\n\nPhysician Notes:\n${section.physicianNotes}`;
  }
  return sectionText;
}).join('\n\n')}`;

    const blob = new Blob([fullNote], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medical-note-${appointmentId}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded medical note');
  };

  // The signed note as the server renders it (patient, visit, codes and signature included)
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const { blob, filename } = await apiService.getBlob(`/v1/clinical/${appointmentId}/medical-note/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'signed-note.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // iOS Safari opens the PDF after click() returns; revoking now would leave it a dead URL.
      setTimeout(() => URL.revokeObjectURL(url), PDF_URL_LIFETIME_MS);
    } catch (err: any) {
      // The error body arrives as a Blob because the request asked for one
      let message = 'Could not download the PDF';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text()).error || message;
        } catch {
          // keep the generic message
        }
      }
      toast.error(message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Generate SOAP note from structured note (local transformation, no API call)
  const generateSoapNote = () => {
    if (!note) return;
    setSoapLoading(true);

    try {
      const soap = formatNoteAsSOAP(note);
      setSoapNote(soap);
      toast.success('SOAP note generated');
    } finally {
      setSoapLoading(false);
    }
  };

  // Format structured note as SOAP (Subjective, Objective, Assessment, Plan)
  const formatNoteAsSOAP = (medicalNote: MedicalNote): string => {
    // SOAP grouping mirrors the backend composition in
    // clinicalResultsDelivery.service.ts: patient-reported/history sections
    // under SUBJECTIVE, clinician-produced content (Physical Exam + Treatment)
    // under OBJECTIVE. The blocks carry no per-section labels, so the Physical
    // Exam display flag does not apply here.
    const subjective = [
      medicalNote.chiefComplaint?.content,
      medicalNote.historyOfPresentIllness?.content,
      medicalNote.pastMedicalHistory?.content,
      medicalNote.medications?.content,
      medicalNote.familyHistory?.content,
      medicalNote.socialHistory?.content,
      medicalNote.reviewOfSystems?.content,
      medicalNote.psychiatricHistory?.content
    ].filter(Boolean).join('\n\n');

    const objective = [
      medicalNote.physicalExam?.content,
      medicalNote.treatment?.content
    ].filter(Boolean).join('\n\n');

    const assessment = medicalNote.assessment?.content || 'See plan below.';
    const plan = medicalNote.plan?.content || 'Not documented.';

    return `SUBJECTIVE:
${subjective || 'Not documented.'}

OBJECTIVE:
${objective || 'Not documented.'}

ASSESSMENT:
${assessment}

PLAN:
${plan}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12">
        <motion.div
          className="flex flex-col items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-xs sm:text-sm text-gray-600 px-4 text-center">Generating medical note...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
        <motion.div
          className="flex flex-col items-center gap-4 max-w-md w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Generation Failed</h3>
          <p className="text-xs sm:text-sm text-gray-600 text-center">{error}</p>
          {!readOnly && (
            <Button
              onClick={generateMedicalNote}
              className="bg-blue-500 hover:bg-blue-600 text-white h-11 sm:h-auto min-w-[44px]"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
        </motion.div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4 gap-1">
        {readOnly ? (
          <>
            <p className="text-sm font-medium text-gray-700 text-center">No note yet</p>
            <p className="text-xs sm:text-sm text-gray-500 text-center">
              The note appears here once the provider generates it.
            </p>
          </>
        ) : (
          <p className="text-xs sm:text-sm text-gray-500 text-center">No medical note available</p>
        )}
      </div>
    );
  }

  const sections = MEDICAL_NOTE_SECTION_ORDER
    .filter(key => note[key] !== undefined) // Only include sections that exist
    .map(key => [key, note[key]] as [keyof MedicalNote, MedicalNoteSection]);
  const capturedSections = sections.filter(([_, section]) => section.status === 'captured');
  // Appointment notes only: encounter notes leave through the EHR
  const signed = isSigned || metadata?.status === 'signed';
  const canDownloadPdf = !isEncounterMode && signed;
  // In standalone the PDF is the export for a signed note, so the text file steps aside
  const showTextDownload = !(canDownloadPdf && operationMode === 'standalone');

  return (
    <div className="space-y-4">
      {/* Actions toolbar */}
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between px-3 py-2.5 lg:px-4 lg:py-2 bg-gray-50 rounded-lg">
        {/* Metadata row */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <FileText className="h-3.5 w-3.5 flex-shrink-0" />
            {metadata?.generatedAt
              ? new Date(metadata.generatedAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                })
              : 'N/A'}
          </span>
          {metadata?.overallConfidence !== undefined && (
            <>
              <span className="text-gray-300 hidden lg:inline">|</span>
              <span className="text-blue-600 font-medium">
                {(metadata.overallConfidence * 100).toFixed(0)}%
                <span className="hidden lg:inline"> confidence</span>
              </span>
            </>
          )}
          <span className="text-gray-300 hidden lg:inline">|</span>
          <span className="text-green-600 font-medium">
            {capturedSections.length}/{sections.length} sections
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-1.5 w-full lg:w-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={copyToClipboard}
            className="min-h-[44px] lg:min-h-[44px] flex-1 lg:flex-none text-xs min-w-[44px]"
            title="Copy to clipboard"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 lg:mr-1" />
                <span className="hidden lg:inline">Copied</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 lg:mr-1" />
                <span className="hidden lg:inline">Copy</span>
              </>
            )}
          </Button>
          {canDownloadPdf && (
            <Button
              size="sm"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="min-h-[44px] lg:min-h-[44px] flex-1 lg:flex-none text-xs min-w-[44px] bg-green-600 hover:bg-green-700 text-white"
              title="Download the signed note as a PDF"
              aria-label="Download PDF"
            >
              <FileDown className="h-3 w-3 lg:mr-1" />
              <span className="hidden lg:inline">{downloadingPdf ? 'Downloading...' : 'Download PDF'}</span>
            </Button>
          )}
          {showTextDownload && (
            <Button
              size="sm"
              variant="outline"
              onClick={downloadNote}
              className="min-h-[44px] lg:min-h-[44px] flex-1 lg:flex-none text-xs min-w-[44px]"
              title="Download note"
            >
              <Download className="h-3 w-3 lg:mr-1" />
              <span className="hidden lg:inline">Download</span>
            </Button>
          )}

          {/* Regenerating writes a new note: providers only */}
          {!readOnly && (
            <Button
              size="sm"
              onClick={generateMedicalNote}
              disabled={loading || !hasPendingChanges}
              className={`min-h-[44px] lg:min-h-[44px] flex-1 lg:flex-none text-xs bg-blue-500 hover:bg-blue-600 text-white min-w-[44px] disabled:opacity-50 disabled:cursor-not-allowed ${hasPendingChanges ? 'ring-2 ring-blue-300 ring-offset-1' : ''}`}
              title={hasPendingChanges ? 'Regenerate note with updated summary' : 'No changes to regenerate'}
            >
              <RefreshCw className={`h-3 w-3 lg:mr-1 ${hasPendingChanges ? 'animate-pulse' : ''}`} />
              <span className="hidden lg:inline">Regenerate</span>
            </Button>
          )}

          <Button
            size="sm"
            onClick={generateSoapNote}
            disabled={soapLoading || !note}
            variant={soapNote ? 'outline' : 'default'}
            className={`min-h-[44px] lg:min-h-[44px] flex-1 lg:flex-none text-xs min-w-[44px] ${!soapNote ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
            title={soapNote ? 'Regenerate SOAP note' : 'Generate SOAP format note'}
          >
            <FileText className="h-3 w-3 lg:mr-1" />
            <span className="hidden lg:inline">SOAP</span>
          </Button>
        </div>
      </div>

      {/* SOAP Note display */}
      {soapNote && (
        <motion.div
          className="border border-amber-200 rounded-lg overflow-hidden bg-amber-50/50"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
        >
          <div className="px-3 py-2 bg-amber-100 font-semibold text-sm text-amber-900 flex items-center justify-between">
            <span>SOAP Note</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(soapNote);
                toast.success('SOAP note copied');
              }}
              className="min-h-[44px] px-3 text-sm text-amber-700 hover:text-amber-900"
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
          </div>
          <div className="px-3 py-3">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
              {soapNote}
            </pre>
          </div>
        </motion.div>
      )}

      {/* Medical note sections */}
      <AnimatePresence>
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {sections.map(([sectionKey, section], index) => (
            <MedicalNoteSection
              key={sectionKey}
              title={MEDICAL_NOTE_SECTION_LABELS[sectionKey]}
              section={section}
              color={MEDICAL_NOTE_SECTION_COLORS[sectionKey]}
              index={index}
            />
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

interface MedicalNoteSectionProps {
  title: string;
  section: MedicalNoteSection;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'pink' | 'yellow' | 'teal' | 'indigo';
  index: number;
}

const MedicalNoteSection: React.FC<MedicalNoteSectionProps> = ({ title, section, color, index }) => {
  const colorClasses: Record<string, string> = {
    blue: 'border-blue-200 bg-blue-50/50',
    green: 'border-green-200 bg-green-50/50',
    orange: 'border-orange-200 bg-orange-50/50',
    purple: 'border-purple-200 bg-purple-50/50',
    pink: 'border-pink-200 bg-pink-50/50',
    yellow: 'border-yellow-200 bg-yellow-50/50',
    teal: 'border-teal-200 bg-teal-50/50',
    indigo: 'border-indigo-200 bg-indigo-50/50'
  };

  const headerColorClasses: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-900',
    green: 'bg-green-100 text-green-900',
    orange: 'bg-orange-100 text-orange-900',
    purple: 'bg-purple-100 text-purple-900',
    pink: 'bg-pink-100 text-pink-900',
    yellow: 'bg-yellow-100 text-yellow-900',
    teal: 'bg-teal-100 text-teal-900',
    indigo: 'bg-indigo-100 text-indigo-900'
  };

  const isCaptured = section.status === 'captured' && section.content;

  return (
    <motion.div
      className={`border rounded-lg overflow-hidden ${colorClasses[color]}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.01 }}
    >
      {/* Card Header - Responsive padding and text sizing */}
      <div className={`px-3 py-2 sm:px-4 font-semibold text-xs sm:text-sm flex items-center justify-between gap-2 ${headerColorClasses[color]}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isCaptured ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
          ) : (
            <XCircle className="h-4 w-4 flex-shrink-0 text-gray-400" />
          )}
          <span className="truncate">{title}</span>
        </div>
        {isCaptured && section.confidence > 0 && (
          <Badge variant="outline" className="text-xs bg-white/50 whitespace-nowrap flex-shrink-0">
            {Math.round(section.confidence * 100)}%
          </Badge>
        )}
      </div>
      {/* Card Body - Responsive padding and minimum text size */}
      <div className="px-3 py-2.5 sm:px-4 sm:py-3">
        {isCaptured ? (
          <p className="text-sm sm:text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {section.content}
          </p>
        ) : (
          <p className="text-sm text-gray-500 italic">
            No information documented in this section
          </p>
        )}
      </div>
    </motion.div>
  );
};
